import { numberName, numberOrder } from "shared/utils/numberName";

import {
  isExcision,
  isLipoma,
  isSkin,
  isSoftTissue,
  isSoftTissueWithSkin,
} from "./conditions";
import { GrossingSelections } from "./selections";

// GrossingConditionals are represented in GrossingSegments with the syntax "[key]", eg: "[subcutaneous]" or "[typeAn]".
// They output different strings depending on the selections the user has made.

export type GrossingConditional = (
  selections: GrossingSelections & { i?: number }
) => string;

export const grossingConditionals: {
  [key: string]: GrossingConditional;
} = {
  typeAn: (s) => (isExcision(s) ? "an" : "a"),
  typeSpace: (s) => (isExcision(s) ? " " : ""),
  grossedComma: ({ tokenSelections }) =>
    tokenSelections.grossed?.value ? ", " : " ",
  grossedAnd: (s) =>
    (s.tokenSelections[`grossed_${s.i}`] || s.tokenSelections.grossed)
      ?.value === ""
      ? ""
      : " and",
  with: ({ modifierSelections, tokenSelections }) =>
    (tokenSelections.type?.value === "excision" &&
      modifierSelections.subcutaneous) ||
    modifierSelections.withA
      ? " with"
      : "",
  subcutaneous: ({ modifierSelections, tokenSelections }) => {
    if (
      tokenSelections.type?.value === "excision" &&
      modifierSelections.subcutaneous
    ) {
      return ` a subcutaneous nodule${modifierSelections.withA ? " and" : ""}`;
    }
    return "";
  },
  specimenName: ({ specimen }) => specimen.name || "",
  softened: ({ modifierSelections }) =>
    modifierSelections.softened ? "Softened" : "",
  softenedAndDecalcified: ({ modifierSelections }) =>
    modifierSelections.softened && modifierSelections.decalcified ? "/" : "",
  decalcified: ({ modifierSelections }) =>
    modifierSelections.decalcified ? "Decalcified" : "",
  i: (s) => `${s.i || 1}`,
  iName: (s) => numberName(s.i || 1),
  iOrder: (s) => numberOrder(s.i || 1),
  bothOrAll: (s) =>
    parseInt(s.tokenSelections.cassette_count?.value || "0") > 2
      ? "All"
      : "Both",
  specimenType: (s) =>
    (isLipoma(s) && isSkin(s)
      ? "skin with lobulated yellow soft adipose tissue"
      : isLipoma(s)
      ? "lobulated yellow soft adipose tissue"
      : isSoftTissue(s)
      ? "soft tissue"
      : "skin") + (isSoftTissueWithSkin(s) ? " with skin" : ""),
  slowMohsAn: (s) =>
    ["annular", "o'shaped"].includes(
      s.tokenSelections.slow_mohs_shape?.value || ""
    )
      ? "an"
      : "a",
  slowMohsExcision: (s) =>
    ["annular", "circular", "o'shaped"].includes(
      s.tokenSelections.slow_mohs_shape?.value || ""
    )
      ? " portion"
      : "",
  fmS: (s) => (s.tokenSelections.fm_cassette_range?.value ? "s" : ""),
  fmIsAre: (s) => (s.tokenSelections.fm_cassette_range?.value ? "are" : "is"),
  clippingsS: (s) =>
    !s.tokenSelections.clipping_count?.value ||
    parseInt(s.tokenSelections.clipping_count.value) > 1
      ? "s"
      : "",
  fbAorAnOrBlank: (s) =>
    s.tokenSelections?.type_fb?.value === "glass"
      ? ""
      : s.tokenSelections?.type_fb?.value === "arthropod"
      ? "an"
      : "a",
  fbInOrBlank: (s) =>
    s.tokenSelections?.medium_fb?.value === "fresh" ? "" : "in",
};
